// The words that name a nationality as free text (SEP-293.4).
//
// Form version 2.0 took the nationality as free text. Form version 3.0 takes
// a country from the list. A draft of version 2.0 gets the country that its
// text names, and this module reads the text. A match is exact after a
// normalisation: the case, the accents, the full stops, "and" against "&"
// and "Saint" against "St" do not matter. A near miss gives null, because a
// guess on a regulatory record is worse than a blank answer.

import { COUNTRIES, COUNTRY_SELECT_PRIMARY_CODE } from './countries';

/** The text in one form, so that two spellings of the same name are equal. */
export function normaliseNationalityWords(text: string): string {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[’‘]/g, "'")
        .replace(/\./g, '')
        .replace(/\band\b/g, '&')
        .replace(/\bsaint\b/g, 'st')
        .replace(/\s*&\s*/g, ' & ')
        .replace(/\s+/g, ' ')
        .replace(/^the /, '')
        .trim();
}

/** The words that name the United Kingdom as a nationality. */
const UNITED_KINGDOM_WORDS: readonly string[] = [
    'british',
    'uk',
    'united kingdom',
    'great britain',
    'gb',
    'english',
    'scottish',
    'welsh',
    'northern irish',
    'united kingdom of great britain & northern ireland',
    'britain',
    'england',
    'scotland',
    'wales',
    'northern ireland',
];

/**
 * The other names of a country: a short form, an older name, and the
 * demonym. Each country that a client of the firm often holds is here, and
 * many others. A country that is not here still matches by its code or its
 * en-GB name.
 */
const COUNTRY_ALIASES: Readonly<Record<string, readonly string[]>> = {
    AF: ['afghan'],
    AL: ['albanian'],
    DZ: ['algerian'],
    AD: ['andorran'],
    AO: ['angolan'],
    AG: ['antiguan', 'antigua'],
    AR: ['argentine', 'argentinian'],
    AM: ['armenian'],
    AU: ['australian'],
    AT: ['austrian'],
    AZ: ['azerbaijani'],
    BS: ['bahamian', 'the bahamas'],
    BH: ['bahraini'],
    BD: ['bangladeshi'],
    BB: ['barbadian', 'bajan'],
    BY: ['belarusian'],
    BE: ['belgian'],
    BZ: ['belizean'],
    BJ: ['beninese'],
    BM: ['bermudian'],
    BT: ['bhutanese'],
    BO: ['bolivian'],
    BA: ['bosnian', 'bosnia', 'bosnia-herzegovina'],
    BW: ['botswanan', 'motswana'],
    BR: ['brazilian'],
    BN: ['bruneian'],
    BG: ['bulgarian'],
    BF: ['burkinabe'],
    BI: ['burundian'],
    KH: ['cambodian'],
    CM: ['cameroonian'],
    CA: ['canadian'],
    CV: ['cape verdean', 'cabo verde'],
    KY: ['caymanian'],
    TD: ['chadian'],
    CL: ['chilean'],
    CN: ['chinese', "people's republic of china", 'prc'],
    CO: ['colombian'],
    CG: ['congolese', 'republic of the congo', 'congo'],
    CD: ['democratic republic of the congo', 'drc'],
    CR: ['costa rican'],
    CI: ['ivorian', 'ivory coast', "cote d'ivoire"],
    HR: ['croatian'],
    CU: ['cuban'],
    CY: ['cypriot'],
    CZ: ['czech', 'czech republic'],
    DK: ['danish', 'dane'],
    DJ: ['djiboutian'],
    DM: ['dominican'],
    DO: ['dominican republic'],
    EC: ['ecuadorian'],
    EG: ['egyptian'],
    SV: ['salvadoran', 'salvadorean'],
    GQ: ['equatoguinean'],
    ER: ['eritrean'],
    EE: ['estonian'],
    SZ: ['swazi', 'swaziland'],
    ET: ['ethiopian'],
    FJ: ['fijian'],
    FI: ['finnish', 'finn'],
    FR: ['french'],
    GA: ['gabonese'],
    GM: ['gambian', 'the gambia'],
    GE: ['georgian'],
    DE: ['german'],
    GH: ['ghanaian'],
    GI: ['gibraltarian'],
    GR: ['greek'],
    GD: ['grenadian'],
    GT: ['guatemalan'],
    GG: ['guernsey'],
    GN: ['guinean'],
    GY: ['guyanese'],
    HT: ['haitian'],
    HN: ['honduran'],
    HK: ['hong kong', 'hongkonger', 'hong kong sar'],
    HU: ['hungarian'],
    IS: ['icelandic', 'icelander'],
    IN: ['indian'],
    ID: ['indonesian'],
    IR: ['iranian', 'persian'],
    IQ: ['iraqi'],
    IE: ['irish', 'republic of ireland', 'eire'],
    IM: ['manx'],
    IL: ['israeli'],
    IT: ['italian'],
    JM: ['jamaican'],
    JP: ['japanese'],
    JE: ['jersey'],
    JO: ['jordanian'],
    KZ: ['kazakh', 'kazakhstani'],
    KE: ['kenyan'],
    XK: ['kosovan', 'kosovar'],
    KW: ['kuwaiti'],
    KG: ['kyrgyz'],
    LA: ['laotian', 'lao'],
    LV: ['latvian'],
    LB: ['lebanese'],
    LS: ['basotho'],
    LR: ['liberian'],
    LY: ['libyan'],
    LI: ['liechtensteiner'],
    LT: ['lithuanian'],
    LU: ['luxembourgish', 'luxembourger'],
    MO: ['macanese', 'macao', 'macau'],
    MG: ['malagasy'],
    MW: ['malawian'],
    MY: ['malaysian'],
    MV: ['maldivian'],
    ML: ['malian'],
    MT: ['maltese'],
    MR: ['mauritanian'],
    MU: ['mauritian'],
    MX: ['mexican'],
    FM: ['micronesian'],
    MD: ['moldovan'],
    MC: ['monegasque', 'monacan'],
    MN: ['mongolian'],
    ME: ['montenegrin'],
    MA: ['moroccan'],
    MZ: ['mozambican'],
    MM: ['burmese', 'burma', 'myanmar'],
    NA: ['namibian'],
    NP: ['nepalese', 'nepali'],
    NL: ['dutch', 'holland', 'the netherlands'],
    NZ: ['new zealander', 'kiwi'],
    NI: ['nicaraguan'],
    NE: ['nigerien'],
    NG: ['nigerian'],
    KP: ['north korean'],
    MK: ['macedonian', 'macedonia'],
    NO: ['norwegian'],
    OM: ['omani'],
    PK: ['pakistani'],
    PS: ['palestinian', 'palestine'],
    PA: ['panamanian'],
    PG: ['papua new guinean'],
    PY: ['paraguayan'],
    PE: ['peruvian'],
    PH: ['filipino', 'philippine'],
    PL: ['polish', 'pole'],
    PT: ['portuguese'],
    QA: ['qatari'],
    RO: ['romanian'],
    RU: ['russian', 'russian federation'],
    RW: ['rwandan'],
    WS: ['samoan'],
    SM: ['sammarinese'],
    SA: ['saudi', 'saudi arabian'],
    SN: ['senegalese'],
    RS: ['serbian'],
    SC: ['seychellois'],
    SL: ['sierra leonean'],
    SG: ['singaporean'],
    SK: ['slovak', 'slovakian'],
    SI: ['slovenian', 'slovene'],
    SO: ['somali'],
    ZA: ['south african'],
    KR: ['south korean', 'korean', 'korea', 'republic of korea'],
    SS: ['south sudanese'],
    ES: ['spanish', 'spaniard'],
    LK: ['sri lankan'],
    KN: ['kittitian', 'st kitts'],
    LC: ['st lucian'],
    VC: ['vincentian'],
    SD: ['sudanese'],
    SR: ['surinamese'],
    SE: ['swedish', 'swede'],
    CH: ['swiss'],
    SY: ['syrian'],
    TW: ['taiwanese'],
    TJ: ['tajik', 'tajikistani'],
    TZ: ['tanzanian'],
    TH: ['thai'],
    TL: ['timorese', 'east timor'],
    TG: ['togolese'],
    TO: ['tongan'],
    TT: ['trinidadian', 'tobagonian', 'trinidad'],
    TN: ['tunisian'],
    TR: ['turkish', 'turkey', 'turkiye'],
    TM: ['turkmen'],
    UG: ['ugandan'],
    UA: ['ukrainian'],
    AE: ['emirati', 'uae'],
    US: ['american', 'us', 'usa', 'united states of america', 'america'],
    UY: ['uruguayan'],
    UZ: ['uzbek', 'uzbekistani'],
    VU: ['ni-vanuatu'],
    VA: ['vatican', 'holy see'],
    VE: ['venezuelan'],
    VN: ['vietnamese', 'viet nam'],
    YE: ['yemeni'],
    ZM: ['zambian'],
    ZW: ['zimbabwean'],
};

const CODE_BY_WORDS: ReadonlyMap<string, string> = (() => {
    const map = new Map<string, string>();
    for (const country of COUNTRIES) {
        map.set(normaliseNationalityWords(country.code), country.code);
        map.set(normaliseNationalityWords(country.name), country.code);
    }
    for (const [code, words] of Object.entries(COUNTRY_ALIASES)) {
        for (const word of words) map.set(normaliseNationalityWords(word), code);
    }
    for (const word of UNITED_KINGDOM_WORDS) {
        map.set(normaliseNationalityWords(word), COUNTRY_SELECT_PRIMARY_CODE);
    }
    return map;
})();

/**
 * The code of the one country that a free-text nationality names, or null.
 * The text must name one country only: "British/Irish" gives null here.
 */
export function countryCodeFromNationality(text: string): string | null {
    const words = normaliseNationalityWords(text);
    if (words === '') return null;
    return CODE_BY_WORDS.get(words) ?? null;
}
